/**
 * Error shapes that consumers can switch on. Axios errors are mapped
 * here so the UI layer doesn't have to crack open AxiosError everywhere.
 */

import type { AxiosError } from 'axios';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: unknown;

  constructor(message: string, status: number, code: string, detail: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }
}

export class NetworkError extends Error {
  constructor(message = 'Network unavailable') {
    super(message);
    this.name = 'NetworkError';
  }
}

/**
 * Map an unknown thrown value (typically from a try/catch around an
 * axios call) into one of our typed errors. Use at React Query
 * `onError` or wherever errors surface.
 */
export function toApiError(err: unknown): ApiError | NetworkError | Error {
  if (err && typeof err === 'object' && 'isAxiosError' in err) {
    const ax = err as AxiosError<{ detail?: string; code?: string }>;
    if (!ax.response) {
      return new NetworkError(ax.message);
    }
    const detail = ax.response.data?.detail ?? ax.message;
    const code = ax.response.data?.code ?? `HTTP_${ax.response.status}`;
    return new ApiError(
      typeof detail === 'string' ? detail : ax.message,
      ax.response.status,
      code,
      ax.response.data,
    );
  }
  return err instanceof Error ? err : new Error(String(err));
}
